/**
 * Ores: the rock you mine in the world and the piece of ore in your pack are built from the same
 * description, so a vein you learn to spot is the ore you get.
 *
 * Every ore has a host rock (its own shape and markings: copper's green crust, iron's red bands,
 * coal's black seams, gold's white quartz) and a cluster of the ore itself standing out of it
 * (copper nuggets, tin crystals, silver wire, glowstone spires). In the world the rock is one
 * instanced mesh and the cluster sits on it in three places that wear away as the vein is worked.
 */
import * as THREE from 'three';

/* ---------------- noise and helpers ---------------- */
function hash3(x: number, y: number, z: number) { const s = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453; return s - Math.floor(s); }
function noise3(x: number, y: number, z: number) {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z), fx = x - ix, fy = y - iy, fz = z - iz;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy), sz = fz * fz * (3 - 2 * fz);
  const l = (a: number, b: number, t: number) => a + (b - a) * t;
  const c = (dx: number, dy: number, dz: number) => hash3(ix + dx, iy + dy, iz + dz);
  return l(l(l(c(0, 0, 0), c(1, 0, 0), sx), l(c(0, 1, 0), c(1, 1, 0), sx), sy), l(l(c(0, 0, 1), c(1, 0, 1), sx), l(c(0, 1, 1), c(1, 1, 1), sx), sy), sz);
}
const fbm = (x: number, y: number, z: number) => noise3(x, y, z) * 0.6 + noise3(x * 2.3, y * 2.3, z * 2.3) * 0.3 + noise3(x * 5.1, y * 5.1, z * 5.1) * 0.1;
/** 1 on a thin line where noise crosses its middle: veins and cracks. */
const ridge = (n: number, w: number) => Math.max(0, 1 - Math.abs(n - 0.5) / w);
const smooth = (a: number, b: number, x: number) => { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const C = (h: number) => new THREE.Color(h);
function mulberry(a: number) { return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

/** Merge geometries (each non-indexed, with a colour per vertex) into one. */
function merge(parts: THREE.BufferGeometry[]) {
  const pos: number[] = [], col: number[] = [];
  for (const g0 of parts) {
    const g = g0.index ? g0.toNonIndexed() : g0, p = g.getAttribute('position'), c = g.getAttribute('color');
    for (let i = 0; i < p.count; i++) { pos.push(p.getX(i), p.getY(i), p.getZ(i)); if (c) col.push(c.getX(i), c.getY(i), c.getZ(i)); else col.push(1, 1, 1); }
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); out.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  out.computeVertexNormals(); return out;
}
/** Colour every vertex of a geometry (made flat-faced first, so each facet takes one colour). */
function paint(g0: THREE.BufferGeometry, f: (p: THREE.Vector3) => THREE.Color, perFace = true) {
  const g = g0.index ? g0.toNonIndexed() : g0, p = g.getAttribute('position'), col: number[] = [], v = new THREE.Vector3();
  for (let i = 0; i < p.count; i += perFace ? 3 : 1) {
    if (perFace) { v.set(0, 0, 0); for (let k = 0; k < 3; k++) v.add(new THREE.Vector3(p.getX(i + k), p.getY(i + k), p.getZ(i + k))); v.divideScalar(3); const c = f(v); for (let k = 0; k < 3; k++) col.push(c.r, c.g, c.b); }
    else { v.set(p.getX(i), p.getY(i), p.getZ(i)); const c = f(v); col.push(c.r, c.g, c.b); }
  }
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3)); g.computeVertexNormals(); return g;
}
const place = (g: THREE.BufferGeometry, p: [number, number, number], r: [number, number, number] = [0, 0, 0], s: number | [number, number, number] = 1) => {
  const m = new THREE.Matrix4().compose(new THREE.Vector3(...p), new THREE.Quaternion().setFromEuler(new THREE.Euler(...r)), typeof s === 'number' ? new THREE.Vector3(s, s, s) : new THREE.Vector3(...s));
  g.applyMatrix4(m); return g;
};
const tint = (g: THREE.BufferGeometry, c: number | ((p: THREE.Vector3) => THREE.Color), perFace = true) => paint(g, typeof c === 'number' ? () => C(c) : c, perFace);

/* ---------------- host rocks ---------------- */
type Shape = 'boulder' | 'slab' | 'blocky' | 'jagged' | 'mound';
interface OreLook {
  shape: Shape;
  /** The host rock's colour at a point (model space, about ±0.6). */
  rock: (p: THREE.Vector3, n: number) => THREE.Color;
  /** The ore's own cluster, roughly 0.3 across, centred at the origin with "up" out of the rock. */
  cluster: (sd: number) => THREE.BufferGeometry;
  /** For icons: how metallic and how rough the ore and the rock look. */
  metal: number; rough: number; rockRough?: number;
  glow?: number; glowAmt?: number; iridescent?: boolean; glassy?: boolean;
}
function hostRock(shape: Shape, seed: number) {
  let g: THREE.BufferGeometry;
  switch (shape) {
    case 'boulder': g = new THREE.IcosahedronGeometry(0.62, 2); break;
    case 'slab': g = new THREE.IcosahedronGeometry(0.62, 2); break;
    case 'blocky': g = new THREE.IcosahedronGeometry(0.62, 3); break;
    case 'jagged': g = new THREE.DodecahedronGeometry(0.62, 2); break;
    case 'mound': g = new THREE.IcosahedronGeometry(0.62, 2); break;
  }
  g = g.index ? g.toNonIndexed() : g;
  g.deleteAttribute('normal'); g.deleteAttribute('uv');
  const merged = mergeVertices(g);
  const p = merged.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) {
    let x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const n = fbm(x * 2.2 + seed, y * 2.2, z * 2.2 + seed * 0.7);
    switch (shape) {
      case 'boulder': { const k = 0.86 + n * 0.32; x *= k; y *= k * 0.92; z *= k; break; }
      case 'slab': { const k = 0.9 + n * 0.22; x *= k * 1.12; z *= k * 1.05; y = y * 0.62 * k; y = Math.round(y * 9) / 9 * 0.7 + y * 0.3; break; } // stacked beds
      case 'blocky': { // squared off towards a block, edges still a little worn
        const m = Math.max(Math.abs(x), Math.abs(y), Math.abs(z)) / 0.62, sq = 1 / Math.pow(m, 0.7), k = (0.92 + n * 0.16) * sq; x *= k * 0.95; y *= k * 0.86; z *= k * 0.9; break; }
      case 'jagged': { const k = 0.8 + n * 0.28 + (hash3(Math.round(x * 50), Math.round(y * 50), Math.round(z * 50)) - 0.5) * 0.2; x *= k * 1.05; y *= k * 0.85; z *= k; break; }
      case 'mound': { const k = 0.88 + n * 0.22; x *= k * 1.1; z *= k * 1.1; y *= k * 0.62; break; }
    }
    if (y < -0.25) y = -0.25 + (y + 0.25) * 0.3; // a flat-ish underside to sit on the ground
    p.setXYZ(i, x, y, z);
  }
  const flat = merged.toNonIndexed(); flat.computeVertexNormals();
  return flat;
}
/** Weld matching corners so displacement doesn't tear the rock open. */
function mergeVertices(g: THREE.BufferGeometry) {
  const p = g.getAttribute('position'), map = new Map<string, number>(), verts: number[] = [], idx: number[] = [];
  for (let i = 0; i < p.count; i++) {
    const k = `${Math.round(p.getX(i) * 1000)},${Math.round(p.getY(i) * 1000)},${Math.round(p.getZ(i) * 1000)}`;
    let j = map.get(k); if (j === undefined) { j = verts.length / 3; map.set(k, j); verts.push(p.getX(i), p.getY(i), p.getZ(i)); }
    idx.push(j);
  }
  const out = new THREE.BufferGeometry(); out.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3)); out.setIndex(idx); return out;
}

/* ---------------- ore clusters ---------------- */
const rnd = (sd: number) => mulberry(Math.floor(sd * 1000) + 7);
function lumpGeo(r: number, amt: number, seed: number, detail = 1) {
  const g = mergeVertices(new THREE.IcosahedronGeometry(r, detail)), p = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) { const x = p.getX(i), y = p.getY(i), z = p.getZ(i), k = 1 + (fbm(x * 9 + seed, y * 9, z * 9 - seed) - 0.5) * 2 * amt; p.setXYZ(i, x * k, y * k, z * k); }
  return g.toNonIndexed();
}
/** A prism of a crystal: n sides, a pointed cap. */
function crystalGeo(r: number, h: number, sides = 6, cap = 0.35) {
  const body = new THREE.CylinderGeometry(r, r * 1.05, h, sides, 1); body.translate(0, h / 2, 0);
  const tip = new THREE.ConeGeometry(r, h * cap, sides, 1); tip.translate(0, h + (h * cap) / 2, 0);
  return [body.toNonIndexed(), tip.toNonIndexed()];
}
function cluster(parts: THREE.BufferGeometry[]) { return merge(parts); }
/** Crystals radiating out of a point. */
function spray(sd: number, n: number, mk: (i: number, r: () => number) => THREE.BufferGeometry[], spread = 0.7) {
  const r = rnd(sd), out: THREE.BufferGeometry[] = [];
  for (let i = 0; i < n; i++) {
    const a = r() * Math.PI * 2, t = r() * spread, q = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.cos(a) * t, r() * 3, Math.sin(a) * t));
    for (const g of mk(i, r)) { g.applyQuaternion(q); g.translate((r() - 0.5) * 0.08, 0, (r() - 0.5) * 0.08); out.push(g); }
  }
  return out;
}
const LOOKS: Record<string, OreLook> = {
  copper: {
    shape: 'boulder', metal: 0.6, rough: 0.35,
    // brown stone crusted green-blue where the copper has weathered
    rock: (p, n) => { const c = C(0x6a5644).multiplyScalar(0.85 + n * 0.3), patina = fbm(p.x * 4 + 3, p.y * 4, p.z * 4); if (patina > 0.55) c.lerp(C(patina > 0.66 ? 0x5ac8a0 : 0x2e8a6a), smooth(0.55, 0.66, patina)); return c; },
    cluster: sd => { const r = rnd(sd); const parts = [0, 1, 2, 3].map(i => tint(place(lumpGeo(0.07 + r() * 0.04, 0.35, sd + i), [(r() - 0.5) * 0.16, 0.04 + r() * 0.03, (r() - 0.5) * 0.16], [r(), r(), r()], [1, 0.75, 1]), p => C(0xd8743a).lerp(C(0xf0a060), smooth(0.02, 0.09, p.y)))); parts.push(tint(place(lumpGeo(0.09, 0.3, sd + 9), [0.06, 0.01, -0.06], [0, 0, 0], [1.3, 0.5, 1.1]), 0x4ab08a)); return cluster(parts); },
  },
  tin: {
    shape: 'slab', metal: 0.75, rough: 0.3,
    // pale grey granite, speckled, in beds
    rock: (p, n) => { const c = C(0xa8a49a).multiplyScalar(0.82 + n * 0.3), s = hash3(Math.round(p.x * 40), Math.round(p.y * 40), Math.round(p.z * 40)); if (s > 0.86) c.set(0x2a2a2a); else if (s < 0.08) c.set(0xf0ece4); if (Math.abs(Math.sin(p.y * 28)) < 0.12) c.multiplyScalar(0.78); return c; },
    cluster: sd => cluster(spray(sd, 5, (i, r) => { const s = 0.05 + r() * 0.035; return [tint(place(new THREE.BoxGeometry(s * 1.3, s * 1.7, s * 1.3).toNonIndexed(), [0, s * 0.8, 0], [r() * 0.6, r() * 3, r() * 0.6]), p => C(0xd8e0e6).multiplyScalar(0.8 + (p.y > 0.05 ? 0.3 : 0)))]; }, 0.9)),
  },
  iron: {
    shape: 'blocky', metal: 0.45, rough: 0.4,
    // banded: red jasper, black and steel-grey layers
    rock: (p, n) => { const band = Math.sin((p.y + Math.sin(p.x * 3 + p.z * 2) * 0.05) * 11), c = band > 0.1 ? C(0xa8402a) : band < -0.6 ? C(0x2e2422) : C(0x7a7a80); if (p.y > 0.18) c.lerp(C(0x8a3422), 0.55); return c.multiplyScalar(0.82 + n * 0.32); },
    cluster: sd => { const r = rnd(sd); return cluster([0, 1, 2, 3, 4].map(i => tint(place(new THREE.SphereGeometry(0.045 + r() * 0.03, 10, 8).toNonIndexed(), [(r() - 0.5) * 0.14, 0.02 + r() * 0.04, (r() - 0.5) * 0.14]), p => C(0x5a5658).lerp(C(0x9a2e1e), smooth(0.03, -0.01, p.y)))).concat([tint(place(new THREE.SphereGeometry(0.08, 12, 8).toNonIndexed(), [0, 0.01, 0], [0, 0, 0], [1.2, 0.6, 1.1]), 0x4a4446)])); },
  },
  coal: {
    shape: 'slab', metal: 0.1, rough: 0.25, rockRough: 0.9,
    // black and dark-grey seams, dusty on top
    rock: (p, n) => { const seam = Math.sin(p.y * 34 + n * 2); const c = seam > 0.3 ? C(0x1a1918) : C(0x3a3836); if (p.y > 0.2) c.lerp(C(0x5a5650), 0.3); return c.multiplyScalar(0.85 + n * 0.25); },
    cluster: sd => cluster(spray(sd, 5, (i, r) => [tint(place(new THREE.OctahedronGeometry(0.075 + r() * 0.035, 0).toNonIndexed(), [0, 0.05, 0], [r(), r(), r()], [1.3, 0.8, 1]), p => C(0x121110).lerp(C(0x4a4a50), smooth(0.05, 0.11, p.y)))], 0.9)),
  },
  silver: {
    shape: 'boulder', metal: 0.65, rough: 0.22,
    // dark slate with white threads running through it
    rock: (p, n) => { const c = C(0x3a3e46).multiplyScalar(0.85 + n * 0.3), v = ridge(fbm(p.x * 3, p.y * 3 + 5, p.z * 3), 0.035); return c.lerp(C(0xe8eef4), v); },
    cluster: sd => { const r = rnd(sd), parts: THREE.BufferGeometry[] = [];
      // wire silver: curling branches
      for (let i = 0; i < 4; i++) { const pts: THREE.Vector3[] = []; let x = (r() - 0.5) * 0.06, y = 0, z = (r() - 0.5) * 0.06; for (let k = 0; k < 6; k++) { pts.push(new THREE.Vector3(x, y, z)); x += (r() - 0.5) * 0.08; y += 0.035 + r() * 0.025; z += (r() - 0.5) * 0.08; } parts.push(tint(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 16, 0.016, 6).toNonIndexed(), 0xeef2f6)); }
      parts.push(tint(place(lumpGeo(0.05, 0.3, sd), [0, 0.01, 0], [0, 0, 0], [1.3, 0.5, 1.3]), 0xc8ccd2)); return cluster(parts); },
  },
  gold: {
    shape: 'boulder', metal: 0.7, rough: 0.25,
    // grey rock with fat veins of white quartz
    rock: (p, n) => { const c = C(0x7a746a).multiplyScalar(0.85 + n * 0.25), v = ridge(fbm(p.x * 2.2 + 1, p.y * 2.2, p.z * 2.2), 0.09); return c.lerp(C(0xf0ece0), smooth(0.2, 0.5, v)); },
    cluster: sd => { const r = rnd(sd); const parts = [tint(place(lumpGeo(0.09, 0.3, sd + 2), [0, 0.01, 0], [0, 0, 0], [1.2, 0.7, 1]), 0xf2eee2)]; for (let i = 0; i < 4; i++) parts.push(tint(place(lumpGeo(0.035 + r() * 0.035, 0.45, sd + i), [(r() - 0.5) * 0.14, 0.05 + r() * 0.03, (r() - 0.5) * 0.14], [r(), r(), r()]), 0xf2c03a)); return cluster(parts); },
  },
  platinum: {
    shape: 'boulder', metal: 0.6, rough: 0.15,
    // dark green-black serpentine, mottled
    rock: (p, n) => { const m = fbm(p.x * 5, p.y * 5, p.z * 5 + 4); return C(0x26322a).lerp(C(0x4a6a52), smooth(0.5, 0.7, m)).multiplyScalar(0.85 + n * 0.25); },
    cluster: sd => { const r = rnd(sd); return cluster([0, 1, 2, 3, 4, 5].map(() => tint(place(new THREE.SphereGeometry(0.04 + r() * 0.035, 14, 10).toNonIndexed(), [(r() - 0.5) * 0.15, 0.02 + r() * 0.04, (r() - 0.5) * 0.15]), 0xf2f4f8, false))); },
  },
  starsilver: {
    shape: 'mound', metal: 0.6, rough: 0.12, glow: 0x7fe8ff, glowAmt: 0.55,
    // blue-grey stone glittering with white points
    rock: (p, n) => { const c = C(0x56647a).multiplyScalar(0.8 + n * 0.3); if (hash3(Math.round(p.x * 30), Math.round(p.y * 30), Math.round(p.z * 30)) > 0.93) c.set(0xe8f8ff); return c; },
    // stars: six thin points from one heart
    cluster: sd => { const r = rnd(sd), parts: THREE.BufferGeometry[] = []; for (let s = 0; s < 2; s++) { const cx = (s - 0.5) * 0.12, cy = 0.07 + s * 0.04; for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2 + r(); parts.push(tint(place(new THREE.ConeGeometry(0.018, 0.12, 4).toNonIndexed(), [cx + Math.cos(a) * 0.05, cy + Math.sin(a) * 0.05, 0.01 * s], [0, 0, a - Math.PI / 2]), 0xd8f6ff)); } parts.push(tint(place(new THREE.OctahedronGeometry(0.03, 0), [cx, cy, 0.01 * s]), 0xffffff)); } return cluster(parts); },
  },
  deepglass: {
    shape: 'jagged', metal: 0.2, rough: 0.05, glow: 0x5ad8ff, glowAmt: 0.35, glassy: true,
    // black volcanic glass, a purple sheen in it
    rock: (p, n) => C(0x1c1826).lerp(C(0x3a2a5a), smooth(0.55, 0.8, fbm(p.x * 3, p.y * 3, p.z * 3))).multiplyScalar(0.8 + n * 0.4),
    cluster: sd => cluster(spray(sd, 5, (i, r) => [tint(place(new THREE.ConeGeometry(0.045 + r() * 0.025, 0.22 + r() * 0.1, 3).toNonIndexed(), [0, 0.1, 0], [0, r() * 3, 0], [1, 1, 0.45]), p => C(0x2a1a48).lerp(C(0x8ae8ff), smooth(0.06, 0.2, p.y) * 0.9))], 0.8)),
  },
  emberheart: {
    shape: 'jagged', metal: 0.1, rough: 0.6, glow: 0xff7a30, glowAmt: 1.0,
    // dark basalt cracked through with fire
    rock: (p, n) => { const c = C(0x2a2220).multiplyScalar(0.8 + n * 0.3), crack = ridge(fbm(p.x * 3.5, p.y * 3.5, p.z * 3.5), 0.04); return c.lerp(C(0xff6a1a), smooth(0.2, 0.7, crack)); },
    cluster: sd => { const r = rnd(sd); return cluster([0, 1, 2].map(i => tint(place(lumpGeo(0.05 + r() * 0.03, 0.4, sd + i), [(r() - 0.5) * 0.1, 0.03, (r() - 0.5) * 0.1], [r(), r(), r()]), p => C(0xffb04a).lerp(C(0xc8300a), smooth(0.02, 0.07, Math.abs(p.x) + Math.abs(p.z)))))); },
  },
  cobalt: {
    shape: 'blocky', metal: 0.5, rough: 0.2, glow: 0x3a6aff, glowAmt: 0.2,
    // grey stone with blue staining
    rock: (p, n) => C(0x66666e).lerp(C(0x3a4ab8), smooth(0.5, 0.68, fbm(p.x * 3 + 2, p.y * 3, p.z * 3)) * 0.85).multiplyScalar(0.82 + n * 0.3),
    cluster: sd => cluster(spray(sd, 6, (i, r) => { const s = 0.07 + r() * 0.04; return [tint(place(new THREE.BoxGeometry(s, s, s).toNonIndexed(), [0, s * 0.7, 0], [Math.PI / 4, r() * 3, Math.PI / 4 * r()]), p => C(0x2a4ae0).lerp(C(0x8aa8ff), smooth(0.04, 0.09, p.y)))]; }, 0.8)),
  },
  tungsten: {
    shape: 'blocky', metal: 0.55, rough: 0.35,
    // heavy dark-grey stone with paler blades through it
    rock: (p, n) => { const c = C(0x4a4a50).multiplyScalar(0.85 + n * 0.25); if (ridge(fbm(p.x * 2.5 + 7, p.y * 2.5, p.z * 2.5), 0.05) > 0.3) c.lerp(C(0x9a9aa2), 0.7); return c; },
    cluster: sd => cluster(spray(sd, 5, (i, r) => [tint(place(new THREE.BoxGeometry(0.026, 0.2 + r() * 0.08, 0.1).toNonIndexed(), [0, 0.1, 0], [0, r() * 3, 0]), p => C(0x3a3a40).lerp(C(0x9a9aa4), smooth(0.04, 0.14, p.y)))], 0.6)),
  },
  blackiron: {
    shape: 'jagged', metal: 0.55, rough: 0.3,
    // near-black rock with a steel edge where it catches the light
    rock: (p, n) => C(0x1c1c22).lerp(C(0x5a5a66), smooth(0.65, 0.85, n)).multiplyScalar(0.9 + n * 0.2),
    cluster: sd => cluster(spray(sd, 5, (i, r) => [tint(place(new THREE.ConeGeometry(0.035 + r() * 0.02, 0.2 + r() * 0.12, 5).toNonIndexed(), [0, 0.1, 0]), p => C(0x2a2a34).lerp(C(0xb8bccc), smooth(0.08, 0.24, p.y)))], 0.9)),
  },
  glowstone: {
    shape: 'mound', metal: 0.1, rough: 0.15, glow: 0x7affc0, glowAmt: 0.9,
    // pale green-grey stone
    rock: (p, n) => C(0x7a9688).lerp(C(0xb8e8d0), smooth(0.6, 0.8, fbm(p.x * 4, p.y * 4, p.z * 4))).multiplyScalar(0.82 + n * 0.25),
    // tall six-sided spires, glowing from inside
    cluster: sd => cluster(spray(sd, 5, (i, r) => crystalGeo(0.022 + r() * 0.015, 0.12 + r() * 0.1, 6, 0.4).map(g => tint(g, p => C(0x4ad8a0).lerp(C(0xe8fff4), smooth(0.05, 0.2, p.y)))), 0.5)),
  },
  iridium: {
    shape: 'boulder', metal: 0.5, rough: 0.15, iridescent: true, glow: 0xc49cff, glowAmt: 0.15,
    // dark stone flecked with every colour
    rock: (p, n) => { const c = C(0x2a2836).multiplyScalar(0.85 + n * 0.3), s = hash3(Math.round(p.x * 26), Math.round(p.y * 26), Math.round(p.z * 26)); if (s > 0.88) c.setHSL((s * 9) % 1, 0.6, 0.6); return c; },
    // thin six-sided plates, each a different hue
    cluster: sd => cluster(spray(sd, 5, (i, r) => { const g = place(new THREE.CylinderGeometry(0.075, 0.075, 0.016, 6).toNonIndexed(), [0, 0.07, 0], [Math.PI / 2 * (0.4 + r() * 0.6), r() * 3, 0]); return [tint(g, C(0).setHSL((0.55 + i * 0.12) % 1, 0.7, 0.62).getHex())]; }, 1)),
  },
};

/* ---------------- what the world and the icons use ---------------- */
const rockCache = new Map<string, THREE.BufferGeometry>(), clusterCache = new Map<string, THREE.BufferGeometry>();
/** Does this rock kind have its own model? */
export const hasOreLook = (kind: string) => kind in LOOKS;
/** The host rock for a kind, in world scale (about 1.2 across before the instance's own scale). */
export function oreRockGeo(kind: string): THREE.BufferGeometry {
  let g = rockCache.get(kind);
  if (!g) {
    const L = LOOKS[kind], sd = kind.length * 1.7;
    const raw = hostRock(L.shape, sd);
    g = paint(raw, p => L.rock(p, fbm(p.x * 6 + sd, p.y * 6, p.z * 6)));
    // darker where the rock meets the ground
    { g.computeBoundingBox(); const lo = g.boundingBox!.min.y, hi = g.boundingBox!.max.y, pp = g.getAttribute('position'), cc = g.getAttribute('color') as THREE.BufferAttribute;
      for (let i = 0; i < pp.count; i++) { const t = (pp.getY(i) - lo) / Math.max(0.001, hi - lo), k = 0.62 + 0.38 * Math.min(1, t / 0.45); cc.setXYZ(i, cc.getX(i) * k, cc.getY(i) * k, cc.getZ(i) * k); } }
    rockCache.set(kind, g);
  }
  return g;
}
/** The ore's cluster for a kind (placed three times on each rock). */
export function oreClusterGeo(kind: string): THREE.BufferGeometry {
  let g = clusterCache.get(kind);
  if (!g) { g = LOOKS[kind].cluster(kind.length * 0.37 + 0.11); g.computeVertexNormals(); clusterCache.set(kind, g); }
  return g;
}
export const oreGlow = (kind: string) => LOOKS[kind]?.glow;
export const oreGlowAmt = (kind: string) => LOOKS[kind]?.glowAmt ?? 0;

/** Where the three clusters sit on a rock: on its upper faces, leaning out. */
export const CLUSTER_SPOTS: [number, number, number, number, number][] = [[0.28, 0.3, 0.2, 0.5, 0.25], [-0.24, 0.32, 0.22, -0.45, 0.3], [0.02, 0.38, -0.24, 0.1, -0.5]];

/** A piece of ore for the pack: a chunk of its rock with its cluster standing out of it. */
export function buildOreItem(kind: string): THREE.Group | null {
  const L = LOOKS[kind]; if (!L) return null;
  const g = new THREE.Group();
  const rockM = new THREE.MeshPhysicalMaterial({ vertexColors: true, roughness: L.rockRough ?? 0.8, metalness: 0.05, flatShading: true, clearcoat: L.glassy ? 1 : 0.1 });
  const oreM = new THREE.MeshPhysicalMaterial({ vertexColors: true, roughness: L.rough, metalness: L.metal, clearcoat: 0.6, iridescence: L.iridescent ? 1 : 0, iridescenceIOR: 1.8,
    emissive: L.glow !== undefined ? C(L.glow) : C(0), emissiveIntensity: (L.glowAmt ?? 0) * 0.8, transparent: !!L.glassy, opacity: L.glassy ? 0.92 : 1 });
  const chunk = new THREE.Mesh(oreRockGeo(kind), rockM); chunk.scale.set(0.72, 0.62, 0.66); g.add(chunk);
  for (const [x, y, z, rx, rz] of CLUSTER_SPOTS) { const c = new THREE.Mesh(oreClusterGeo(kind), oreM); c.position.set(x * 0.72, y * 0.62 + 0.02, z * 0.66 + 0.05); c.rotation.set(rx, 0, rz); c.scale.setScalar(1.25); g.add(c); }
  g.rotation.set(0.35, 0.5, 0);
  return g;
}
/** Item ids for ores, to their rock kind. */
export const ORE_ITEM_KIND: Record<string, string> = {
  copper_ore: 'copper', tin_ore: 'tin', iron_ore: 'iron', coal: 'coal', silver_ore: 'silver', gold_ore: 'gold', platinum_ore: 'platinum', starsilver_ore: 'starsilver',
  deepglass_ore: 'deepglass', ember_ore: 'emberheart', cobalt_ore: 'cobalt', tungsten_ore: 'tungsten', blackiron_ore: 'blackiron', glowstone: 'glowstone', iridium_ore: 'iridium',
};
